/* eslint-disable func-names */
import { actionChannel, call, delay, race, take } from 'redux-saga/effects';

import { DictionariesActionsEnum } from 'CorePackage/DictionariesModule/enums/DictionariesActionsEnum';

export const globalDictionaryTracker = {
  activeRequests: new Set<string>(),
  completedRequests: new Set<string>(),
  listeners: new Set<Function>(),

  addRequest(requestId: string) {
    this.activeRequests.add(requestId);

    this.notifyListeners();
  },

  completeRequest(requestId: string) {
    if (this.activeRequests.has(requestId)) {
      this.activeRequests.delete(requestId);
      this.completedRequests.add(requestId);

      this.notifyListeners();
    }
  },

  reset() {
    this.activeRequests.clear();
    this.completedRequests.clear();

    this.notifyListeners();
  },

  addListener(listener: Function) {
    this.listeners.add(listener);
  },

  removeListener(listener: Function) {
    this.listeners.delete(listener);
  },

  notifyListeners() {
    this.listeners.forEach(listener => listener());
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* dictionaryHandlerSaga(): Generator<any, void, any> {
  // Создаем каналы для всех типов действий связанных с API
  const successChannel = yield actionChannel(DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_SUCCESS);
  const failureChannel = yield actionChannel(DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_FAILURE);
  const noDataChannel = yield actionChannel(DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_NO_DATA);
  const alreadyChannel = yield actionChannel(DictionariesActionsEnum.DICTIONARIES_ITEM_VALUE_GET_ALREADY_LOAD);

  try {
    while (true) {
      const result = yield race({
        success: take(successChannel),
        failure: take(failureChannel),
        noData: take(noDataChannel),
        already: take(alreadyChannel),
      });

      // Обработка УСПЕШНОГО завершения
      if (result.success) {
        const { dictionaryUUID, dictionaryVersionUUID } = result.success.payload;

        globalDictionaryTracker.completeRequest(`${dictionaryUUID}/${dictionaryVersionUUID}`);
      }

      // Обработка ОШИБКИ
      if (result.failure) {
        const { dictionaryUUID, dictionaryVersionUUID } = result.failure.payload;

        globalDictionaryTracker.completeRequest(`${dictionaryUUID}/${dictionaryVersionUUID}`);
      }

      // Обработка пустого ответа
      if (result.noData) {
        const { dictionaryUUID, dictionaryVersionUUID } = result.noData.payload;

        globalDictionaryTracker.completeRequest(`${dictionaryUUID}/${dictionaryVersionUUID}`);
      }

      // Обработка ранее загруженных данных
      if (result.already) {
        const { dictionaryUUID, dictionaryVersionUUID } = result.already.payload;

        globalDictionaryTracker.completeRequest(`${dictionaryUUID}/${dictionaryVersionUUID}`);
      }
    }
  } finally {
    successChannel.close();
    failureChannel.close();
    noDataChannel.close();
    alreadyChannel.close();
  }
}

/**
 * Канал для ожидания завершения всех Dictionary вызовов
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* createDictionaryWaitChannel(): Generator<any, { completed: number; total: number }, any> {
  const POLL_INTERVAL = 100; // Проверяем каждые 100мс
  // Предохранитель, на случай, если какой-то запрос так и не завершится,
  // не блокируем UI навсегда. В норме сюда не попадаем.
  const SAFETY_TIMEOUT = 30000; // 30 секунд

  return yield race({
    // Завершаем, как только не осталось активных запросов.
    // Проверка выполняется до первого delay, поэтому при отсутствии запросов
    // (например, у новой КНМ из плана нет справочников для загрузки) канал
    // завершается немедленно, не дожидаясь таймаута.
    completion: call(function* () {
      while (true) {
        if (globalDictionaryTracker.activeRequests.size === 0) {
          return {
            completed: globalDictionaryTracker.completedRequests.size,
            total: globalDictionaryTracker.completedRequests.size,
          };
        }
        yield delay(POLL_INTERVAL);
      }
    }),

    // Страховочный таймаут на случай «зависшего» запроса
    timeout: call(function* () {
      yield delay(SAFETY_TIMEOUT);

      return {
        completed: globalDictionaryTracker.completedRequests.size,
        total: globalDictionaryTracker.completedRequests.size + globalDictionaryTracker.activeRequests.size,
      };
    }),
  });
}
